// ProjectsPage.jsx
import React, { useState } from 'react';

const projectsData = [
  {
    id: 1,
    title: 'Roadrunner Connect',
    description:
      'A full stack app development internship at MSU Denver where we built a scalable, multi-feature application for campus engagement. The app is meant to gamify the process of going to on-campus events through integrating a check-in system, raffles, and leaderboards based on a currency obtained by attending events. As a full stack developer on this project, I mainly focused on the in-app map interface using GoogleMaps, and the calendar parser that is integral to obtaining event data for the app. With a team of 5, I worked hard over a 10 week period with 2 week sprints in an Agile team. I used a wide variety of technologies such as GoogleMaps API, GoogleCloud API, Flutter, spaCy NLP, Geolocation, ExpressJs, MongoDB, Mongoose, Docker, and more to complete my portion of this project. This was a great experience where I could truly understand what it\'s like working with a team of developers on a common goal.',
    shortDescription: 'A full stack app development internship at MSU Denver...',
    image: require('../assets/rrc.png'),
    date: 'Jun 2024 - Aug 2024',
  },
  {
    id: 2,
    title: 'Undergraduate Research',
    description:
      'Built and tested a Raspberry Pi Cluster to improve computation and learn parallelism in practice. The research started as the testing of a Raspberry Pi running the same program in different languages and comparing their completion times. For this, I wrote benchmarking code in ARM Assembly, Java, and Python and observed their differences as languages, such as time taken in overhead computing. When more funding was obtained, I pivoted the project into building and testing a Raspberry Pi cluster to explore the benefits and limitations of parallelization using message passing. For my testing I used Python, Java, MPI, and Linux shell scripts.',
    shortDescription: 'Built and tested a Raspberry Pi Cluster to improve computation and learn parallelism in practice...',
    image: require('../assets/raspberryPi.png'),
    date: 'Aug 2023 - Jun 2024',
    github: 'https://github.com/vlvcDev/PiClusterPrograms'
  },
  {
    id: 3,
    title: 'SpiderByte',
    description:
      'A hackathon-winning website that aims to offer coding challenges based on course material. The website was built based on the prompt "Something to help the school", and we decided to make something that was practical for students to get extracurricular experience in Computer Science. The website organizes it\'s coding challenges based on courses such as CS1050, and features an embedded code editor. Code sent is tested against test cases to validate their completion. Furthermore, users have access to a learning tool for Python that will visualize the objects and variables as the program steps through each line of code. The technologies used were Flask, Pythontutor, CodeMirror, and SQLite.',
    shortDescription: 'A hackathon-winning website that aims to make practicing...',
    image: require('../assets/hackathon.png'),
    date: 'Oct 2023',
    github: 'https://github.com/cecehoush/BitsPlease-SpiderByte',
  },
  {
    id: 4,
    title: 'Physarum Slime Mold Simulation',
    description:
      'Slime Mold is a fascinating organism that can make hypnotizing organic patterns. This project is a simulation of the slime mold\'s behavior using GLSL shaders in OpenFrameworks. The simulation spawns hundreds of thousands of agents with navigation based on the trails of other agents with a variable degree of randomness. The agents are attracted to higher light densities, and tweaking the parameters allows for a very wide variety of movement patterns. After writing this program the first time with the Processing framework, I learned why GPUs are useful. I decided to make a compute shader to leverage the parallelization that GPUs have to offer, and it benefitted the performance of the program by miles.',
    shortDescription: 'Slime Mold is a fascinating organism that can make hypnotizing organic patterns...',
    image: require('../assets/slimeshot.png'),
    date: 'Nov 2023',
    github: 'https://github.com/vlvcDev/Physarum-GLSL',
  },
  {
    id: 5,
    title: 'Ray Tracing Model',
    description:
      'Raytracing has a lot to do with Linear Algebra, and this project is a model of a raytracer that I made in response to a Linear Algebra assignment. The program will visually cast rays from the center of the viewport, then the rays will eventually reach a target distance and display color. If the ray hit an object, the pixel mapped to that array will be red. If a ray didn\'t intersect with anything, then the pixel displays green. This project was a great way to understand the math behind raytracing and how it can be applied to computer graphics.',
    shortDescription: 'Raytracing has a lot to do with Linear Algebra...',
    image: require('../assets/raytrace.png'),
    date: 'Dec 2023',
    github: 'https://github.com/vlvcDev/Ray-Tracing-Model',
  },
  {
    id: 6,
    title: 'Flutter GPT Replica',
    description:
      'In order to prepare for my app development internship interview, I decided to learn Flutter and build an app. Instead of creating something brand new, I make a replica of ChatGPT. When I say replica, I don\'t mean a carbon copy of the design and functionality. I just made a chatbot app using API calls to OpenAI\'s GPT-4. The app can store conversations, remembers past messages and allows for multi-shot prompting with them, and stores all of this information through Firebase and Firestore. This project was a great way to learn Flutter and how to use APIs in mobile development.',
    shortDescription: 'In order to prepare for my app development...',
    image: require('../assets/chatlogo.png'),
    date: 'Apr 2024',
    github: 'https://github.com/vlvcDev/ChatAppFlutter',
  },
];

const ProjectsPage = () => {
  const [expandedProjectId, setExpandedProjectId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedProjectId(expandedProjectId === id ? null : id);
  };

  const calculateHeight = () => {
    const baseHeight = 30; // 30vh base for each project
    const additionalHeight = 20; // Additional 20vh for padding or spacing
    return `${projectsData.length * baseHeight + additionalHeight}vh`;
  };

  return (
    <div style={{ ...styles.pageContainer, minHeight: calculateHeight() }}>
      <h1 style={styles.pageTitle}>Projects</h1>
      <div style={styles.container}>
        {projectsData.map((project) => (
          <div
            key={project.id}
            style={styles.projectCard}
            onClick={() => toggleExpand(project.id)}
          >
            <div style={styles.imageContainer}>
              <img
                src={project.image}
                alt={project.title}
                style={styles.projectImage}
              />
              {/* GitHub Button below the image */}
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.githubButton}
                  onClick={(e) => e.stopPropagation()}
                >
                  GitHub
                </a>
              )}
            </div>
            <div style={styles.projectContent}>
              <h2 style={styles.projectTitle}>{project.title}</h2>
              <p style={styles.projectDate}>
                {project.date}
              </p>
              <p style={styles.projectDescription}>
                {expandedProjectId === project.id
                  ? project.description
                  : project.shortDescription}
              </p>
              {expandedProjectId !== project.id && (
                <span style={styles.readMore}>Read more...</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    width: '100vw',
    backgroundColor: '#0a0a0a',
    paddingTop: '50px',
    paddingBottom: '100px',
    scrollbarWidth: 'none',
  },
  pageTitle: {
    textAlign: 'center',
    color: '#fff',
    fontSize: '3rem',
    fontWeight: 'bold',
    margin: '20px 0',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  projectCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '15px',
    borderRadius: '10px',
    border: '2px solid #fff',
    backgroundColor: '#000',
    color: '#fff',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginRight: '20px',
  },
  projectImage: {
    width: '150px',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  githubButton: {
    padding: '8px 12px',
    backgroundColor: '#0a0a0a',
    textDecoration: 'none',
    borderRadius: '5px',
    border: '1px solid #fff',
    color: '#fff',
    fontSize: '0.9rem',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    transition: 'background-color 0.3s ease, color 0.3s ease',
  },
  projectContent: {
    flex: 1,
  },
  projectTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
  },
  projectDate: {
    fontSize: '0.9rem',
    color: '#fff',
    marginBottom: '10px',
  },
  projectDescription: {
    fontSize: '1rem',
    marginBottom: '10px',
  },
  readMore: {
    color: '#007BFF',
    cursor: 'pointer',
  },
  '@media (max-width: 768px)': {
    projectCard: {
      flexDirection: 'column',
      alignItems: 'center',
    },
    imageContainer: {
      alignItems: 'center',
      marginRight: '0',
      marginBottom: '15px',
    },
    projectImage: {
      width: '100%',
      height: '200px',
      marginBottom: '10px',
    },
    githubButton: {
      alignSelf: 'center',
    },
  },
};

export default ProjectsPage;
export { projectsData };
